import { useHistory, useLocation, useParams } from "react-router";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useManageFeatureInstanceDetails } from "entity-app/hooks/feature-details-page";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  CompanyParticipationType,
  ESubmittalStatus,
  ProjectPermissionEnum,
  UserParticipationType
} from "constants/index";
import { AttachmentDoc } from "models/submittal-log";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { getSubmittalStatus, isProjectInIntegrationMode } from "utils/utils";
import { message } from "utils/ciq-message";
import { MUTATION_UPDATE_ATTACHMENT_BY_PK } from "services/graphQL/mutations";
import { generateS3URL } from "services/file-mgmt";
import { noAuthClient } from "services/axios";
import { getEditableFields } from "components/submittal-details/helpers";
import { FeatureTypes } from "entity-app/constants";
import { hasTheFeature } from "entity-app/utils/utils";
import usePageTitle from "hooks/use-page-title";
import { formatEntityTitle } from "config/page-titles";
import useFeatureLogNavReader from "hooks/use-feature-log-nav-reader";
import {
  getValidatedSubmittalLogReturnTo,
  getSubmittalDetailsHeaderIconFeature,
  getSubmittalLogRoute
} from "pages/submittal-log/submittal-log-view";
import FeatureHeader from "./submittal-detail-header";
import SubmittalDetailsCards from "./submittal-details-cards";
import SubmittalAttachments from "../submittal-attachment";

const findCompanyParticipant = (
  participants: any[] | undefined,
  participationTypeId: CompanyParticipationType
) =>
  participants?.find(
    (company) => company?.company_participation_type_id === participationTypeId
  );

const findUserParticipant = (
  participants: any[] | undefined,
  participationTypeId: UserParticipationType
) =>
  participants?.find(
    (user) => user?.user_participation_type?.id === participationTypeId
  );

const getAssigneeCompanyName = (assignee: any) =>
  assignee?.user?.project_users?.length
    ? (assignee?.user?.project_users[0].subscription_vendor?.name ?? "")
    : "";

const buildSourceInfoParticipant = (participant: any) => ({
  user: {
    id: `${participant?.user_source_info?.first_name} ${participant?.user_source_info?.last_name}`,
    first_name: `${participant?.user_source_info?.first_name} ${participant?.user_source_info?.last_name}`,
    vendor_source_info: participant?.user_source_info?.vendor_source_info
  }
});

const buildUnregisteredParticipant = (participant: any) => ({
  user: {
    id: participant.user_unregistered,
    first_name: participant.user_unregistered
  }
});

const resolveUserParticipant = (
  participant: any,
  options: { includeUnregistered?: boolean } = {}
) => {
  if (!participant) {
    return null;
  }

  if (participant.user) {
    return participant;
  }

  if (options.includeUnregistered && participant.user_unregistered) {
    return buildUnregisteredParticipant(participant);
  }

  if (participant.user_source_info) {
    return buildSourceInfoParticipant(participant);
  }

  return null;
};

const buildAssigneeUnregistered = (
  assignee: any,
  drReviewer: any,
  submitter: any,
  drCompany: any,
  contractor: any
) => {
  if (!assignee?.user_unregistered) {
    return null;
  }

  if (assignee.user_unregistered === drReviewer?.user_unregistered) {
    return {
      id: `${drReviewer?.user_unregistered}`,
      fullName: `${drReviewer?.user_unregistered}`,
      company: {
        name: drCompany?.vendor_unregistered ?? drCompany?.vendor?.name ?? ""
      },
      type: "poc"
    };
  }

  return {
    id: `${submitter?.user_unregistered}`,
    fullName: `${submitter?.user_unregistered}`,
    company: {
      name: contractor?.vendor_unregistered ?? contractor?.vendor?.name ?? ""
    },
    type: "poc"
  };
};

function NewSubmittalDetailsPage() {
  const history = useHistory();
  const { projectId, submittalId } = useParams() as any;
  const {
    tokenContents,
    gqlClientForProject,
    projectDetails,
    tokenRetrievalState,
    eventLogs,
    columnHeaders: { submittalHeaderMap },
    projectIntegrationConfig,
    isFeatureFlagEnabled
  } = useContext(ProjectContext);
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get("tab");
  const [tabIndex, setTabIndex] = useState<string>(tab || "1");
  const paramAttachmentId = new URLSearchParams(location.search).get("aId");
  const previousEventLogs = useRef(eventLogs);

  const { featureInstanceData, actions } = useManageFeatureInstanceDetails(
    submittalId,
    FeatureTypes.SUBMITTALS
  );

  usePageTitle(
    formatEntityTitle(
      featureInstanceData?.auto_inc_value,
      featureInstanceData?.title
    )
  );
  const navigator = useFeatureLogNavReader({
    feature: "submittals",
    projectId,
    currentId: submittalId
  });

  const [isRevisionMode, setRevisionMode] = useState(false);
  const [isWorkflowInOverrideMode, setIsWorkflowInOverrideMode] =
    useState(false);
  const [localFeatureInstanceData, setLocalFeatureInstanceData] =
    useState<any>();

  useEffect(() => {
    if (!featureInstanceData) return;
    setLocalFeatureInstanceData(structuredClone(featureInstanceData));
  }, [featureInstanceData]);

  const submittal = useMemo(() => {
    if (!localFeatureInstanceData?.feature_submittals?.length) return null;
    return localFeatureInstanceData?.feature_submittals[0];
  }, [localFeatureInstanceData?.feature_submittals]);

  const [submittalViewState, setSubmittalViewState] = useState<{
    status: number;
    userType: string;
  }>({
    status: submittal?.status,
    userType: tokenContents?.userType as string
  });

  const cannotRespondToWorkflow: boolean = useMemo(() => {
    return isPermissionNotGrantted(
      ProjectPermissionEnum.SubmittalWorkflow,
      tokenContents?.role ?? ""
    );
  }, [tokenContents]);

  const isAttachementFeatureEnabled =
    !isFeatureFlagEnabled("DISABLE_ATTACHMENT");

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const [addMore, setAddMore] = useState(false);

  const [deleteAttachmentMutationPK] = useCIQMutation(
    MUTATION_UPDATE_ATTACHMENT_BY_PK,
    { client: gqlClientForProject }
  );

  const attachmentDocuments = useMemo(() => {
    const attachments: AttachmentDoc[] = (
      localFeatureInstanceData?.attachments || []
    ).filter(
      (attachment: AttachmentDoc & { comment_id?: string }) =>
        !attachment.comment_id
    );
    return {
      documents: attachments.filter((x) => !x.reference_id),
      referenceDocs: attachments.filter((y) => y.reference_id)
    };
  }, [localFeatureInstanceData?.attachments]);

  const [selectedDocument, setSelectedDocument] = useState<
    AttachmentDoc | undefined
  >();

  const goBackToList = () => {
    const returnTo = getValidatedSubmittalLogReturnTo(
      projectId,
      location.state
    );
    if (returnTo) {
      history.push(returnTo);
      return;
    }
    if (localFeatureInstanceData?.deleted_from_source) {
      history.push(getSubmittalLogRoute(projectId, "deleted"));
      return;
    }
    if (submittal?.workflow_status === ESubmittalStatus.VOID) {
      history.push(getSubmittalLogRoute(projectId, "voided"));
      return;
    }
    if (!localFeatureInstanceData?.is_tracked) {
      history.push(`/project/${projectId}/submittals-untracked`);
      return;
    }
    history.push(`/project/${projectId}/submittals`);
  };

  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const shouldRefetchAttachments = eventLogs.some((e) => {
        const info: any = e?.info;
        const isAttachmentOrCommentEvent =
          e?.data_source === "attachments" ||
          e?.data_source === "comment" ||
          info?.table_name === "attachments" ||
          info?.table_name === "comment";
        if (!isAttachmentOrCommentEvent) {
          return false;
        }
        return (
          hasTheFeature(submittalId, e) ||
          attachmentDocuments.documents.some((x) => x.id === e.object_id)
        );
      });

      if (shouldRefetchAttachments) {
        actions.refetchFeatureInstanceData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [actions, attachmentDocuments.documents, eventLogs, submittalId]);
  const submittalParticipants = useMemo(() => {
    const companyParticipants =
      featureInstanceData?.feature_company_participants;
    const userParticipants = featureInstanceData?.feature_user_paticipants;

    const contractor = findCompanyParticipant(
      companyParticipants,
      CompanyParticipationType.RESPONSIBLE_CONTRACTOR
    );
    const drCompany = findCompanyParticipant(
      companyParticipants,
      CompanyParticipationType.DESIGN_REVIEWER_VENDOR
    );
    const assignee = findUserParticipant(
      userParticipants,
      UserParticipationType.ASSIGNEE
    );
    const submitter = findUserParticipant(
      userParticipants,
      UserParticipationType.SUBMITTER
    );
    const gcReviewer = findUserParticipant(
      userParticipants,
      UserParticipationType.GC_REVIEWER
    );
    const drReviewer = findUserParticipant(
      userParticipants,
      UserParticipationType.DESIGN_REVIEWER
    );

    return {
      contractor,
      assignee: resolveUserParticipant(assignee),
      submitter: resolveUserParticipant(submitter, {
        includeUnregistered: true
      }),
      gcReviewer: resolveUserParticipant(gcReviewer),
      drReviewer: resolveUserParticipant(drReviewer, {
        includeUnregistered: true
      }),
      drCompany,
      assigneeCompany: getAssigneeCompanyName(assignee),
      assigneeUnregistered: () =>
        buildAssigneeUnregistered(
          assignee,
          drReviewer,
          submitter,
          drCompany,
          contractor
        )
    };
  }, [
    featureInstanceData?.feature_company_participants,
    featureInstanceData?.feature_user_paticipants
  ]);

  const editableFields: string[] = useMemo(() => {
    if (!submittal) return [];
    const tmpEditableFields = getEditableFields(
      submittalViewState,
      localFeatureInstanceData,
      tokenContents,
      submittal,
      submittalParticipants
    );
    if (isWorkflowInOverrideMode) {
      tmpEditableFields.push("attachment");
    }
    return tmpEditableFields;
  }, [
    submittalViewState,
    localFeatureInstanceData,
    tokenContents,
    isWorkflowInOverrideMode,
    submittal,
    submittalParticipants
  ]);

  useEffect(() => {
    if (
      gqlClientForProject &&
      tokenRetrievalState?.success &&
      featureInstanceData &&
      tokenContents &&
      submittal
    ) {
      if (!isRevisionMode && tokenContents) {
        const submittalStatus = getSubmittalStatus(
          submittal?.submittal_status_history_details
        );

        setSubmittalViewState({
          userType: tokenContents.userType,
          status: submittalStatus || ESubmittalStatus.CREATE
        });
      }
    }
  }, [
    submittal,
    gqlClientForProject,
    tokenRetrievalState,
    featureInstanceData,
    tokenContents,
    isRevisionMode
  ]);

  const RemoveAttachment = async (attachmentId: string) => {
    try {
      const updateResponse = await deleteAttachmentMutationPK({
        variables: {
          id: attachmentId,
          set: { deleted: true }
        }
      });

      if (updateResponse?.data) {
        message.success("Removed successfully");
        if (paramAttachmentId) {
          history.replace({
            pathname: `/project/${projectId}/submittals/${submittalId}`,
            search: "?tab=2",
            state: location.state
          });
        }
        if (selectedDocument && selectedDocument?.id === attachmentId) {
          setSelectedDocument(undefined);
        }
      }
      if (updateResponse?.errors) {
        message.error(updateResponse.errors[0].message);
      }
    } catch (ex: any) {
      message.error(ex.message);
    }
  };

  const getLatestWFHistoryItem = (historyItems: any) => {
    // Unreachable: sole caller passes `submittal_status_history_details || []`, always a truthy array.
    /* v8 ignore next */
    if (!historyItems) return null;
    const historyArr = historyItems.filter(
      (historyItem: any) =>
        !historyItem.action?.toLowerCase().includes("deleted")
    );
    if (historyArr && historyArr?.length > 0) {
      return historyArr[0];
    }
    return null;
  };

  const getOnBehalfOfInfoObj = () => {
    let onBehalfOfInfoObj: any = null;
    if (isWorkflowInOverrideMode) {
      onBehalfOfInfoObj = {};
      if (submittalParticipants?.assignee?.user?.id) {
        onBehalfOfInfoObj.on_behalf_of_user_id =
          submittalParticipants?.assignee?.user?.id;
      } else if (submittalParticipants?.assigneeUnregistered()) {
        onBehalfOfInfoObj.on_behalf_of_user_id =
          submittalParticipants?.assigneeUnregistered()?.id;
      }
    }
    return onBehalfOfInfoObj;
  };

  const uploadAttachment = async ({ file, onSuccess }: any) => {
    let requestPayload = {
      feature: "SUBMITTALS",
      feature_id: submittalId,
      file_name: file.name
    };
    const onBehalfOfInfo = getOnBehalfOfInfoObj();
    if (onBehalfOfInfo) {
      requestPayload = { ...requestPayload, ...onBehalfOfInfo };
    }

    const historyItem = getLatestWFHistoryItem(
      submittal?.submittal_status_history_details || []
    );

    const subHistoryObj: any = {};
    if (historyItem) {
      subHistoryObj.submittal_status_history_id = `${historyItem.id}`;
      requestPayload = {
        ...requestPayload,
        ...subHistoryObj
      };
    }
    const signedUrlRespose: any = await generateS3URL(
      requestPayload,
      tokenRetrievalState.token
    );

    if (signedUrlRespose.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
    }

    const fileUploadResponse: any = await noAuthClient.put(
      `${signedUrlRespose.data.success.url}`,
      file,
      {
        onUploadProgress: () => {}
      }
    );
    if (fileUploadResponse.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
      // reject(new Error(fileUploadResponse.error));
    }
    onSuccess("OK");
    return fileUploadResponse;
  };

  const findLatestAttachmentVersion = useCallback(
    (doc: AttachmentDoc) => {
      const latestVersion =
        doc.versions.length === 0 ? doc : doc.versions.slice(-1)[0];
      const lastRef = attachmentDocuments?.referenceDocs.find(
        (d) => d.reference_id === latestVersion.id
      );
      return lastRef || latestVersion;
    },
    [attachmentDocuments?.referenceDocs]
  );

  const submittalCardContainer = () => {
    switch (tabIndex) {
      case "1":
        return (
          <SubmittalDetailsCards
            featureInstanceData={featureInstanceData}
            actions={actions}
            featureId={submittalId}
            setTabIndex={setTabIndex}
            setAddMore={setAddMore}
            workflowOverride={{
              isWorkflowInOverrideMode,
              setIsWorkflowInOverrideMode
            }}
            isRevisionMode={isRevisionMode}
            submittal={submittal}
            editableFields={editableFields}
            updateFeatureData={{
              localFeatureInstanceData,
              setLocalFeatureInstanceData
            }}
            submittalViewState={submittalViewState}
          />
        );
      case "2":
        return (
          <SubmittalAttachments
            editableFields={isRevisionMode ? [] : editableFields}
            documents={attachmentDocuments?.documents}
            referenceDocuments={attachmentDocuments?.referenceDocs}
            UploadAttachment={uploadAttachment}
            RemoveAttachment={RemoveAttachment}
            disabled={false}
            selectedDocument={selectedDocument}
            setSelectedDocument={(attachment: any) => {
              setSelectedDocument(attachment);
              if (paramAttachmentId) {
                history.replace({
                  pathname: `/project/${projectId}/submittals/${submittalId}`,
                  search: "?tab=2",
                  state: location.state
                });
              }
            }}
            findLatestAttachmentVersion={findLatestAttachmentVersion}
            isIntegrationMode={isIntegrationMode}
            addMore={addMore}
          />
        );
      default:
        return "";
    }
  };

  return (
    <div className="grow flex-col bg-ciq-gray-50">
      <FeatureHeader
        featureInstanceData={featureInstanceData}
        title="Submittal Details"
        subTitle=""
        actions={actions}
        number={localFeatureInstanceData?.auto_inc_value}
        headerTitle={localFeatureInstanceData?.title}
        disabled={
          !isRevisionMode &&
          !isIntegrationMode &&
          !cannotRespondToWorkflow &&
          editableFields.includes("title")
        }
        goBackToList={goBackToList}
        feature={getSubmittalDetailsHeaderIconFeature({
          deletedFromSource: localFeatureInstanceData?.deleted_from_source,
          isVoid: submittal?.workflow_status === ESubmittalStatus.VOID,
          isTracked: localFeatureInstanceData?.is_tracked
        })}
        getTabIndex={setTabIndex}
        submittal={submittal}
        projectIntegrationConfig={projectIntegrationConfig}
        isIntegrationMode={isIntegrationMode}
        isAttachementFeatureEnabled={isAttachementFeatureEnabled}
        setRevisionMode={setRevisionMode}
        submittalHeaderMap={submittalHeaderMap}
        tokenContents={tokenContents}
        setSubmittalViewState={setSubmittalViewState}
        updateFeatureData={{
          localFeatureInstanceData,
          setLocalFeatureInstanceData
        }}
        gqlClientForProject={gqlClientForProject}
        isRevisionMode={isRevisionMode}
        navigator={navigator}
      />
      {submittalCardContainer()}
    </div>
  );
}

export default NewSubmittalDetailsPage;
